import { useMemo } from 'react';
import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query';
import { queryClient } from '@/config/reactQuery';
import { AppSearch, DamageExceptionModel } from '@/types';
import { QUERY_KEYS } from '../../queryKeys';
import { damageExceptions } from '../DamageExceptions';

const serializeDamageExceptionsQuery = (q: AppSearch<DamageExceptionModel>) =>
  JSON.stringify({
    name: q.name,
    search: q.search,
    page: q.page,
    pageSize: q.pageSize,
    sortField: q.sortField,
    sortOrder: q.sortOrder,
    filters: q.filters,
  });

const prepareDamageExceptionsSearchBody = (
  queryData: AppSearch<DamageExceptionModel>,
): AppSearch<DamageExceptionModel> => {
  const body: AppSearch<DamageExceptionModel> = JSON.parse(
    JSON.stringify(queryData),
  );
  body.filters?.forEach((filter) => {
    filter.value = filter.value.map((v) =>
      v instanceof Date ? v.toISOString() : v?.toString(),
    );
  });
  return body;
};

const invalidateDamageExceptionListAndDetail = (id: number) => {
  void queryClient.invalidateQueries({
    queryKey: [QUERY_KEYS.damageExceptionsList],
  });
  void queryClient.invalidateQueries({
    queryKey: [QUERY_KEYS.damageExceptions, id],
  });
};

export const useDamageExceptions = (
  query: AppSearch<DamageExceptionModel> | undefined,
) => {
  const serialized = useMemo(
    () => (query ? serializeDamageExceptionsQuery(query) : '__idle__'),
    [query],
  );

  return useQuery({
    queryKey: [QUERY_KEYS.damageExceptionsList, serialized] as const,
    queryFn: async () => {
      const body = prepareDamageExceptionsSearchBody(query!);
      const response = await damageExceptions.getDamageExceptions(body);
      return response?.data;
    },
    enabled: !!query,
    placeholderData: keepPreviousData,
  });
};

export const fetchDamageExceptionsForExport = async (
  query: AppSearch<DamageExceptionModel>,
): Promise<DamageExceptionModel[]> => {
  const exportQuery = { ...query };
  exportQuery.page = undefined;
  exportQuery.pageSize = undefined;
  const body = prepareDamageExceptionsSearchBody(
    exportQuery as AppSearch<DamageExceptionModel>,
  );
  const response = await damageExceptions.getDamageExceptions(body);
  return response?.data?.entities ?? [];
};

export const invalidateDamageExceptionsList = () =>
  queryClient.invalidateQueries({
    queryKey: [QUERY_KEYS.damageExceptionsList],
  });

export const useDamageExceptionFilters = () =>
  useQuery({
    queryKey: [QUERY_KEYS.damageExceptions, 'filters'],
    queryFn: async () =>
      (await damageExceptions.getDamageExceptionFilters()).data,
  });

export const useDamageExceptionById = (id?: number) =>
  useQuery({
    queryKey: [QUERY_KEYS.damageExceptions, id],
    queryFn: async () =>
      (await damageExceptions.getDamageExceptionById(id!)).data,
    enabled: !!id,
  });

export const useCreateDamageExceptionNote = (id: number) =>
  useMutation({
    mutationFn: damageExceptions.createDamageExceptionNote,
    onSuccess: () => invalidateDamageExceptionListAndDetail(id),
  });

export const useCreateDamageExceptionAction = (id: number) =>
  useMutation({
    mutationFn: damageExceptions.createDamageExceptionAction,
    onSuccess: () => invalidateDamageExceptionListAndDetail(id),
  });

export const useUpdateDamageExceptionStatus = (id: number) =>
  useMutation({
    mutationFn: damageExceptions.updateDamageExceptionStatus,
    onSuccess: () => invalidateDamageExceptionListAndDetail(id),
  });

export const useUploadDamageExceptionAttachment = (id: number) =>
  useMutation({
    mutationFn: damageExceptions.uploadDamageExceptionAttachment,
    onSuccess: () => invalidateDamageExceptionListAndDetail(id),
  });
